import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-white font-semibold hover:bg-primary/90 transition-colors shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-primary/10 hover:text-foreground hover:border-primary/50 transition-colors",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        
        /* Updated Color System - Green for Primary Actions, Yellow for Navigation */
        nav: "bg-[#FFD600] text-black font-semibold hover:bg-[#E6C200] hover:border-black/20 border border-transparent transition-all duration-200 shadow-sm",
        highlight: "bg-[#FFD600] text-black font-semibold hover:bg-[#E6C200] hover:border-black/20 border border-transparent transition-all duration-200 shadow-sm",
        action: "bg-primary text-white font-semibold hover:bg-primary/90 transition-colors shadow-sm",
        
        /* Legacy LANERUNNER variants */
        "lr-yellow": "bg-[#FFD600] text-black font-semibold hover:bg-[#E6C200] hover:border-black/20 border border-transparent transition-all duration-200 shadow-sm",
        "lr-green": "bg-primary text-white font-semibold hover:bg-primary/90 transition-colors shadow-sm",
        "lr-cyan": "bg-primary text-white font-semibold hover:bg-primary/90 transition-colors shadow-sm",
        "lr-ghost": "bg-transparent text-muted-foreground hover:bg-card hover:text-foreground border border-border transition-colors",
        "lr-outline": "bg-transparent text-foreground border border-primary hover:bg-primary hover:text-white transition-colors",
        "lr-danger": "bg-lr-status-danger text-white hover:bg-lr-status-danger/80 transition-colors",
        "lr-success": "bg-primary text-white font-semibold hover:bg-primary/90 transition-colors shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "lr-compact": "h-8 px-3 text-xs",
        "lr-wide": "h-12 px-12 text-base font-semibold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
